import { el, mount, type ElChild } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState, renderEmptyState } from "../components/states";
import { renderStatusText } from "../components/statusText";
import { renderSecretReveal } from "../components/secretReveal";
import { confirmAction } from "../components/confirmDialog";
import { openModal } from "../components/modal";
import { showToast } from "../components/toast";
import {
  HELP,
  columnsTooltipWidthCh,
  renderColumnsInfoTooltip,
  renderInfoTooltip,
} from "../components/tooltip";
import {
  filterAccessGroups,
  renderAccessGroupPicker,
  syncAccessGroupPickerUi,
} from "../components/accessGroupPicker";
import { absoluteTime, entityLabel, sortAccessGroups } from "../utils/format";
import {
  disableService,
  getService,
  listGroups,
  patchService,
  refreshServiceKey,
  revokeServiceKey,
} from "../services/admin";
import { ApiError } from "../services/apiClient";
import type { AuthGroup, ServiceAccount } from "../types/api";

export class ServiceDetailPage {
  private body = el("div", {}, [renderLoadingState("Loading service account…")]);
  private service: ServiceAccount | null = null;
  private groups: AuthGroup[] = [];
  private selectedGroupIds = new Set<number>();
  private editingGroups = false;
  private savingGroups = false;
  private groupQuery = "";

  constructor(private id: string) {}

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Service detail",
          subtitle: "Access, assigned groups, and the state of this service's API key.",
          back: { fallbackPath: "/services", navigate },
          actions: [
            el(
              "button",
              { className: "btn btn-ghost btn-sm", type: "button", onclick: () => void this.load() },
              ["Refresh"]
            ),
          ],
        }),
        this.body,
      ])
    );
    void this.load();
  }

  destroy(): void {}

  private async load(): Promise<void> {
    mount(this.body, renderLoadingState("Loading service account…"));
    try {
      [this.service, this.groups] = await Promise.all([getService(this.id), listGroups()]);
      this.groups = sortAccessGroups(this.groups);
      this.selectedGroupIds = new Set(this.service.groups.map((group) => group.id));
      this.editingGroups = false;
      this.render();
    } catch (err) {
      mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
    }
  }

  private render(): void {
    if (!this.service) return;
    const service = this.service;

    mount(
      this.body,
      el("div", { className: "service-profile" }, [
        this.renderOverviewPanel(service),
        this.renderGroupsPanel(),
        this.renderCredentialsPanel(),
      ])
    );
  }

  private renderOverviewPanel(service: ServiceAccount): HTMLElement {
    const status = renderStatusText(
      service.isActive ? "Active" : "Inactive",
      service.isActive ? "success" : "muted"
    );

    return el("div", { className: "panel panel--standard service-profile__overview" }, [
      el("p", { className: "panel__title" }, ["Overview"]),
      el("section", { className: "service-profile__summary", "aria-label": "Service summary" }, [
        el("p", { className: "service-profile__name" }, [service.name]),
        el("div", { className: "service-profile__status" }, [status]),
      ]),
      el("section", { className: "service-profile__meta", "aria-label": "Service metadata" }, [
        profileField("Service ID", el("span", { className: "mono tnum" }, [String(service.id)])),
        profileField("Created At", timestampValue(service.createdAt)),
        profileField("Updated At", timestampValue(service.updatedAt)),
      ]),
      el("div", { className: "service-profile__actions" }, [
        el(
          "button",
          {
            className: service.isActive ? "btn btn-danger-ghost btn-sm" : "btn btn-ghost btn-sm",
            type: "button",
            onclick: () => void this.setActive(!service.isActive),
          },
          [service.isActive ? "Disable Service" : "Enable Service"]
        ),
      ]),
    ]);
  }

  private renderGroupsPanel(): HTMLElement {
    const header = el(
      "div",
      { className: "service-profile__groups-header" },
      [
        el("div", {}, [
          el("p", { className: "panel__title label-with-help" }, [
            "Assigned access groups",
            renderInfoTooltip(HELP.accessGroup),
          ]),
          el("p", { className: "panel__description" }, [
            "Access groups control which data types and fields this service can see.",
          ]),
        ]),
        !this.editingGroups
          ? el(
              "button",
              {
                className: "btn btn-ghost btn-sm",
                type: "button",
                onclick: () => this.startEditingGroups(),
              },
              ["Edit access groups"]
            )
          : null,
      ]
    );

    return el("div", { className: "panel panel--dominant service-profile__groups" }, [
      header,
      this.editingGroups ? this.renderGroupsEditor() : this.renderGroupsSummary(),
    ]);
  }

  private renderGroupsSummary(): HTMLElement {
    if (!this.service?.groups.length) {
      return renderEmptyState("No access groups assigned. Use Edit access groups to grant access.");
    }

    const assigned = sortAccessGroups(this.service.groups);
    const tipWidth = columnsTooltipWidthCh(assigned);
    return el(
      "ul",
      { className: "service-profile__group-list" },
      assigned.map((group) =>
        el(
          "li",
          { className: "service-profile__group-item" },
          [
            el("span", { className: "label-with-help" }, [
              group.name,
              renderColumnsInfoTooltip(group.columns, { widthCh: tipWidth }),
            ]),
            el("span", { className: "service-profile__group-meta" }, [entityLabel(group.entity)]),
          ]
        )
      )
    );
  }

  private renderGroupsEditor(): HTMLElement {
    const content = renderAccessGroupPicker({
      groups: this.groups,
      selectedIds: this.selectedGroupIds,
      query: this.groupQuery,
      disabled: this.savingGroups,
      constrainListHeight: true,
      onQueryChange: (query) => {
        this.groupQuery = query;
      },
      onToggle: (groupId) => {
        if (this.selectedGroupIds.has(groupId)) this.selectedGroupIds.delete(groupId);
        else this.selectedGroupIds.add(groupId);
        syncAccessGroupPickerUi(this.body, this.groups, this.selectedGroupIds, this.groupQuery);
      },
      onToggleVisible: (select) => {
        const visible = filterAccessGroups(this.groups, this.groupQuery);
        for (const group of visible) {
          if (select) this.selectedGroupIds.add(group.id);
          else this.selectedGroupIds.delete(group.id);
        }
        syncAccessGroupPickerUi(this.body, this.groups, this.selectedGroupIds, this.groupQuery);
      },
    });

    return el("div", {}, [
      content,
      this.groups.length
        ? el(
            "div",
            { className: "form-actions form-actions--end form-actions--mt-7" },
            [
              el(
                "button",
                {
                  className: "btn btn-ghost btn-sm",
                  type: "button",
                  disabled: this.savingGroups,
                  onclick: () => this.cancelEditingGroups(),
                },
                ["Cancel"]
              ),
              el(
                "button",
                {
                  className: "btn btn-primary btn-sm",
                  type: "button",
                  disabled: this.savingGroups,
                  onclick: () => void this.saveGroups(),
                },
                [this.savingGroups ? "Saving…" : "Save access groups"]
              ),
            ]
          )
        : el("div", { className: "form-actions form-actions--end form-actions--mt-7" }, [
            el(
              "button",
              {
                className: "btn btn-ghost btn-sm",
                type: "button",
                onclick: () => this.cancelEditingGroups(),
              },
              ["Done"]
            ),
          ]),
    ]);
  }

  private startEditingGroups(): void {
    if (!this.service) return;
    this.selectedGroupIds = new Set(this.service.groups.map((group) => group.id));
    this.groupQuery = "";
    this.editingGroups = true;
    this.render();
  }

  private cancelEditingGroups(): void {
    if (!this.service) return;
    this.selectedGroupIds = new Set(this.service.groups.map((group) => group.id));
    this.groupQuery = "";
    this.editingGroups = false;
    this.render();
  }

  private renderCredentialsPanel(): HTMLElement {
    const service = this.service!;
    return el("div", { className: "panel panel--standard service-profile__credentials" }, [
      el("p", { className: "panel__title" }, ["Credentials"]),
      el("p", { className: "panel__description" }, [
        "Keys are shown in full only once, right after you create or refresh them.",
      ]),
      profileField(
        "API Key",
        renderStatusText(service.hasApiKey ? "Present" : "Revoked", service.hasApiKey ? "success" : "muted")
      ),
      el("div", { className: "service-profile__actions" }, [
        el(
          "button",
          { className: "btn btn-ghost btn-sm", type: "button", onclick: () => void this.refreshKey() },
          ["Refresh API Key"]
        ),
        el(
          "button",
          {
            className: "btn btn-danger-ghost btn-sm",
            type: "button",
            disabled: !service.hasApiKey,
            onclick: () => void this.revokeKey(),
          },
          ["Revoke API Key"]
        ),
      ]),
    ]);
  }

  private async setActive(active: boolean): Promise<void> {
    if (!this.service) return;
    if (!active) {
      const confirmed = await confirmAction({
        title: `Disable ${this.service.name}?`,
        message: "This service will no longer be able to pull data from the platform.",
        confirmLabel: "Disable Service",
        danger: true,
      });
      if (!confirmed) return;
    }

    try {
      this.service = active ? await patchService(this.id, { isActive: true }) : await disableService(this.id);
      this.selectedGroupIds = new Set(this.service.groups.map((group) => group.id));
      showToast(active ? "Service enabled." : "Service disabled.", "success");
      this.render();
    } catch (err) {
      showToast(errorMessage(err), "error");
    }
  }

  private async saveGroups(): Promise<void> {
    if (!this.service || this.savingGroups) return;
    this.savingGroups = true;
    this.render();
    try {
      this.service = await patchService(this.id, { groupIds: Array.from(this.selectedGroupIds) });
      this.selectedGroupIds = new Set(this.service.groups.map((group) => group.id));
      this.editingGroups = false;
      showToast("Assigned access groups updated.", "success");
    } catch (err) {
      showToast(errorMessage(err), "error");
    } finally {
      this.savingGroups = false;
      this.render();
    }
  }

  private async refreshKey(): Promise<void> {
    if (!this.service) return;
    const confirmed = await confirmAction({
      title: `Refresh API key for ${this.service.name}?`,
      message: "The current key will stop working immediately. The replacement key is shown only once.",
      confirmLabel: "Refresh API Key",
      danger: true,
    });
    if (!confirmed) return;

    try {
      const result = await refreshServiceKey(this.id);
      openModal(renderSecretReveal(result.apiKey, `New API key for "${this.service.name}"`), {
        title: "API key refreshed",
      });
      showToast("API key refreshed.", "success");
      await this.load();
    } catch (err) {
      showToast(errorMessage(err), "error");
    }
  }

  private async revokeKey(): Promise<void> {
    if (!this.service) return;
    const confirmed = await confirmAction({
      title: `Revoke API key for ${this.service.name}?`,
      message:
        "The current key will stop working immediately. You can create a replacement later with Refresh API Key.",
      confirmLabel: "Revoke API Key",
      danger: true,
    });
    if (!confirmed) return;

    try {
      this.service = await revokeServiceKey(this.id);
      showToast("API key revoked.", "success");
      this.render();
    } catch (err) {
      showToast(errorMessage(err), "error");
    }
  }
}

function profileField(label: ElChild, value: HTMLElement): HTMLElement {
  return el("div", { className: "service-profile__field" }, [
    el("div", { className: "service-profile__field-label" }, [label]),
    el("div", { className: "service-profile__field-value" }, [value]),
  ]);
}

function timestampValue(iso: string | null | undefined): HTMLElement {
  return el("span", { className: "service-profile__timestamp", title: iso ?? undefined }, [
    absoluteTime(iso),
  ]);
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this service account.";
}
